import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { categoryConfigs, getStatusBadge, getColorClass, ProjectCategory, InventoryItem } from "@/mocks/projectsData";
import { getStatusBadgeColor, InventoryField, defaultInventoryFields } from "@/mocks/inventoryFieldsData";
import { getSellingTypeBgClass, getSellingTypeColorClass, SellingType, defaultSellingTypes } from "@/mocks/sellingTypesData";
import { fetchApi } from "@/services/apiService";
import { useToast } from "@/components/base/Toast";
import { exportToCSV, formatExportDate } from "@/utils/exportUtils";
import { getScoreBadgeColors, getScoreLabel } from "@/mocks/leadsData";

type TabKey = "overview" | "leads" | "pipeline" | "inventory" | "documents" | "analytics" | "quotations";

const fileIconMap: Record<string, string> = {
  PDF: "ri-file-pdf-line text-red-500",
  XLSX: "ri-file-excel-2-line text-green-600",
  XLS: "ri-file-excel-2-line text-green-600",
  DOCX: "ri-file-word-2-line text-blue-600",
  DOC: "ri-file-word-2-line text-blue-600",
  IMAGE: "ri-image-line text-purple-600",
  JPG: "ri-image-line text-purple-600",
  PNG: "ri-image-line text-purple-600",
};

const defaultPipelineStages = [
  { name: "New Inquiry", color: "bg-gray-400" },
  { name: "Site Visit Scheduled", color: "bg-blue-500" },
  { name: "Site Visit Done", color: "bg-indigo-500" },
  { name: "Proposal Sent", color: "bg-amber-500" },
  { name: "Negotiation", color: "bg-pink-500" },
  { name: "Booking Done", color: "bg-green-500" },
  { name: "Lost", color: "bg-red-400" },
];

const getFieldKey = (f: InventoryField): string => {
  if (f.fieldKey) return f.fieldKey;
  return f.label.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_{2,}/g, '_').replace(/^_|_$/g, '');
};

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<any>(null);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [sellingTypes, setSellingTypes] = useState<SellingType[]>([]);
  const [inventoryFields, setInventoryFields] = useState<InventoryField[]>([]);
  const [allLeads, setAllLeads] = useState<any[]>([]);
  const [pipelineStages, setPipelineStages] = useState<any[]>(defaultPipelineStages);
  const [linkedQuotations, setLinkedQuotations] = useState<any[]>([]);

  // Modals state
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [itemFormFields, setItemFormFields] = useState<Record<string, string>>({});

  // Link lead to unit modal
  const [linkLeadItemId, setLinkLeadItemId] = useState<string | null>(null);
  const [leadSearchQuery, setLeadSearchQuery] = useState("");

  // Link lead to project modal
  const [showLinkLeadModal, setShowLinkLeadModal] = useState(false);
  const [projectLeadSearch, setProjectLeadSearch] = useState("");

  // Add Document Modal
  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("PDF");
  const [docSize, setDocSize] = useState("1.5 MB");
  const [docUrl, setDocUrl] = useState("");

  // Add Task inline/modal
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskAssigned, setNewTaskAssigned] = useState("");
  const [newTaskDueDate, setNewTaskDueDate] = useState("");
  const [newTaskPriority, setNewTaskPriority] = useState("Medium");
  const [agentsList, setAgentsList] = useState<string[]>([
    "Rajesh Kumar", "Priya Sharma", "Amit Patel", "Sneha Rao", "Admin"
  ]);

  // Edit Project Form State
  const [editName, setEditName] = useState("");
  const [editStatus, setEditStatus] = useState("Active");
  const [editColor, setEditColor] = useState("indigo");
  const [editCategory, setEditCategory] = useState<ProjectCategory>("properties");
  const [editSellingTypeId, setEditSellingTypeId] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editClientName, setEditClientName] = useState("");
  const [editBudget, setEditBudget] = useState("");
  const [editSpent, setEditSpent] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editDueDate, setEditDueDate] = useState("");
  const [editCustomFields, setEditCustomFields] = useState<Record<string, string>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadProjectData = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [projRes, typesRes, fieldsRes, leadsRes, stagesRes, quotesRes, usersRes] = await Promise.all([
        fetchApi<any>(`/projects/${projectId}`),
        fetchApi<SellingType[]>('/projects/settings/selling-types', undefined, []),
        fetchApi<InventoryField[]>('/projects/settings/inventory-fields', undefined, []),
        fetchApi<any>('/leads?limit=200', undefined, []),
        fetchApi<any>('/config/pipeline-stages/get-all', undefined, []),
        fetchApi<any>('/quotations', undefined, []),
        fetchApi<any>('/auth/company-user/all', undefined, []),
      ]);

      if (projRes) {
        setProject(projRes);
        setItems(Array.isArray(projRes.inventory) ? projRes.inventory : []);
        setTasks(Array.isArray(projRes.tasks) ? projRes.tasks : []);
        setDocuments(Array.isArray(projRes.documents) ? projRes.documents : []);
        setLinkedQuotations(Array.isArray(projRes.linkedQuotations) ? projRes.linkedQuotations : (Array.isArray(quotesRes) ? quotesRes : (quotesRes as any)?.data || []));
      }
      const rawTypes = Array.isArray(typesRes) ? typesRes : (typesRes as any)?.data || [];
      setSellingTypes(rawTypes.length > 0 ? rawTypes : defaultSellingTypes);

      const rawFields = Array.isArray(fieldsRes) ? fieldsRes : (fieldsRes as any)?.data || [];
      setInventoryFields(rawFields.length > 0 ? rawFields : defaultInventoryFields);
      
      const rawLeads = Array.isArray(leadsRes)
        ? leadsRes
        : Array.isArray(leadsRes?.items)
        ? leadsRes.items
        : Array.isArray(leadsRes?.data?.items)
        ? leadsRes.data.items
        : Array.isArray(leadsRes?.data)
        ? leadsRes.data
        : [];
      setAllLeads(rawLeads);

      const rawStages = Array.isArray(stagesRes) ? stagesRes : (stagesRes as any)?.data || [];
      if (Array.isArray(rawStages) && rawStages.length > 0) {
        setPipelineStages(rawStages);
      }

      // Parse agents from usersRes
      const userList = Array.isArray(usersRes)
        ? usersRes
        : Array.isArray(usersRes?.items)
        ? usersRes.items
        : Array.isArray(usersRes?.data?.items)
        ? usersRes.data.items
        : Array.isArray(usersRes?.data)
        ? usersRes.data
        : [];
      const names: string[] = [];
      userList.forEach((u: any) => {
        if (u.name) names.push(u.name);
        else if (u.fullName) names.push(u.fullName);
        else if (u.username) names.push(u.username);
        if (u.leader_name) names.push(u.leader_name);
        if (Array.isArray(u.member_names)) names.push(...u.member_names);
      });
      // Also grab agents from leads
      rawLeads.forEach((l: any) => {
        if (l.assigned) names.push(l.assigned);
      });
      const uniqueAgents = Array.from(new Set(names)).filter(Boolean);
      if (uniqueAgents.length > 0) {
        setAgentsList(uniqueAgents);
        if (!newTaskAssigned) setNewTaskAssigned(uniqueAgents[0]);
      }
    } catch (err: any) {
      console.error("Failed to load project details:", err);
      showToast(err.message || "Failed to load project details", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjectData();
  }, [projectId]);

  // Selling Type & Field Configs
  const sellingType = useMemo(() => {
    if (!project) return null;
    const stId = project.sellingTypeId || project.selling_type_id;
    const list = sellingTypes.length > 0 ? sellingTypes : defaultSellingTypes;
    if (stId) {
      const found = list.find(
        (st) => String(st.id) === String(stId) || st.name.toLowerCase() === String(stId).toLowerCase()
      );
      if (found) return found;
    }
    if (project.category) {
      const catMatch = list.find((st) => st.name.toLowerCase().includes(String(project.category).toLowerCase()));
      if (catMatch) return catMatch;
    }
    return list[0] || null;
  }, [project, sellingTypes]);

  const allAvailableFields: InventoryField[] = useMemo(() => {
    return inventoryFields && inventoryFields.length > 0 ? inventoryFields : defaultInventoryFields;
  }, [inventoryFields]);

  const sellingTypeFields: InventoryField[] = useMemo(() => {
    if (sellingType && Array.isArray(sellingType.inventoryFieldIds) && sellingType.inventoryFieldIds.length > 0) {
      const matched = allAvailableFields.filter((f) => {
        const fKey = getFieldKey(f);
        return sellingType.inventoryFieldIds?.some((fid) => {
          const fidStr = String(fid).toLowerCase();
          return (
            String(f.id).toLowerCase() === fidStr ||
            (f.fieldKey && f.fieldKey.toLowerCase() === fidStr) ||
            fKey === fidStr ||
            (f.label && f.label.toLowerCase() === fidStr)
          );
        }) && f.active !== false;
      });

      if (matched.length > 0) {
        return matched;
      }
    }

    const active = allAvailableFields.filter((f) => f.active !== false);
    return active.length > 0 ? active : defaultInventoryFields;
  }, [sellingType, allAvailableFields]);

  const inventoryListColumns = useMemo(() => {
    const cols = sellingTypeFields
      .filter((f) => f.showInList !== false)
      .sort((a, b) => (a.order || 1) - (b.order || 1));
    if (cols.length > 0) return cols;
    return defaultInventoryFields.filter((f) => f.showInList !== false);
  }, [sellingTypeFields]);

  const inventoryFormFields = useMemo(() => {
    const fields = sellingTypeFields
      .filter((f) => f.showInForm !== false)
      .sort((a, b) => (a.order || 1) - (b.order || 1));
    if (fields.length > 0) return fields;
    return defaultInventoryFields.filter((f) => f.showInForm !== false);
  }, [sellingTypeFields]);

  const stLabel = sellingType?.itemLabel || "Unit";
  const stAvailableStatus = sellingType?.availableStatus || "Available";
  const stSoldStatus = sellingType?.soldStatus || "Sold";

  // Dynamic calculations
  const totalUnitsCount = items.length;
  const soldUnitsCount = items.filter((it) => {
    const s = (it.status || "").toLowerCase();
    return s === stSoldStatus.toLowerCase() || s === "sold" || s === "booked" || s === "enrolled" || s === "active clients" || s === "out of stock";
  }).length;
  const availableUnitsCount = Math.max(0, totalUnitsCount - soldUnitsCount);

  const dynamicProgress = totalUnitsCount > 0 ? Math.round((soldUnitsCount / totalUnitsCount) * 100) : (project?.progress || 0);

  // Leads linked to this project
  const projectLeads = useMemo(() => {
    if (!project) return [];
    return allLeads.filter(
      (l) =>
        String(l.projectId) === String(projectId) ||
        String(l.project_id) === String(projectId) ||
        String(l.project_id) === String(project.name) ||
        String(l.projectId) === String(project.name)
    );
  }, [allLeads, project, projectId]);

  // Handle Edit Project Modal open
  const openEditModal = () => {
    if (!project) return;
    setEditName(project.name || "");
    setEditStatus(project.status || "Active");
    setEditColor(project.color || "indigo");
    setEditCategory(project.category || "properties");
    setEditSellingTypeId(project.sellingTypeId || project.selling_type_id || "");
    setEditDesc(project.description || "");
    setEditClientName(project.client_name || "");
    setEditBudget(project.budget ? String(project.budget) : "");
    setEditSpent(project.spent ? String(project.spent) : "");
    setEditStartDate(project.start_date || "");
    setEditDueDate(project.due_date || "");
    setEditCustomFields(project.customFields || {});
    setShowEditProjectModal(true);
  };

  const handleSaveProjectEdit = async () => {
    if (!editName.trim()) {
      showToast("Project name is required", "error");
      return;
    }

    const payload = {
      name: editName.trim(),
      status: editStatus,
      color: editColor,
      category: editCategory,
      sellingTypeId: editSellingTypeId || null,
      description: editDesc,
      client_name: editClientName.trim() || null,
      budget: parseFloat(editBudget) || 0,
      spent: parseFloat(editSpent) || 0,
      start_date: editStartDate || null,
      due_date: editDueDate || null,
      customFields: editCustomFields,
    };

    try {
      const updated = await fetchApi<any>(`/projects/${projectId}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      setProject((prev: any) => ({ ...prev, ...payload, ...updated }));
      showToast("Project details updated successfully", "success");
      setShowEditProjectModal(false);
    } catch (err: any) {
      showToast(err.message || "Failed to update project", "error");
    }
  };

  const handleDeleteProject = async () => {
    try {
      await fetchApi(`/projects/${projectId}`, { method: "DELETE" });
      showToast("Project deleted successfully", "success");
      navigate("/projects");
    } catch (err: any) {
      showToast(err.message || "Failed to delete project", "error");
    }
  };

  // Open Add Inventory Item Modal
  const openAddInventoryItem = () => {
    const initial: Record<string, string> = {};
    inventoryFormFields.forEach((f) => {
      const k = getFieldKey(f);
      if (k === "status" || f.label.toLowerCase() === "status") {
        initial[k] = stAvailableStatus || (f.options && f.options[0]) || "Available";
      } else {
        initial[k] = "";
      }
    });
    setItemFormFields(initial);
    setEditingItemId(null);
    setShowAddItemModal(true);
  };

  const openEditInventoryItem = (item: InventoryItem) => {
    setEditingItemId(item.id);
    const initial: Record<string, string> = {};
    inventoryFormFields.forEach((f) => {
      const k = getFieldKey(f);
      if (item[k] !== undefined) {
        initial[k] = String(item[k]);
      } else if (item[f.fieldKey] !== undefined) {
        initial[k] = String(item[f.fieldKey]);
      } else if (item[f.label] !== undefined) {
        initial[k] = String(item[f.label]);
      } else {
        initial[k] = "";
      }
    });
    if (item.status) initial.status = item.status;
    if (item.buyer) initial.buyer = item.buyer;
    if (item.leadName) initial.leadName = item.leadName;
    setItemFormFields(initial);
    setShowAddItemModal(true);
  };

  // Inventory Item Save
  const handleSaveInventoryItem = async () => {
    const required = inventoryFormFields.filter((f) => f.required);
    const missing = required.find((f) => {
      const k = getFieldKey(f);
      return !itemFormFields[k] || !String(itemFormFields[k]).trim();
    });
    if (missing) {
      showToast(`Please enter ${missing.label}`, "error");
      return;
    }

    const payload: Record<string, any> = {
      ...itemFormFields,
      status: itemFormFields.status || stAvailableStatus || "Available",
    };

    if (editingItemId) {
      try {
        const updated = await fetchApi<InventoryItem>(
          `/projects/${projectId}/inventory/${editingItemId}`,
          {
            method: "PUT",
            body: JSON.stringify(payload),
          }
        );
        setItems((prev) =>
          prev.map((it) => (it.id === editingItemId ? { ...it, ...payload, ...updated } : it))
        );
        showToast(`${stLabel} updated successfully`, "success");
      } catch (err: any) {
        showToast(err.message || `Failed to update ${stLabel}`, "error");
      }
    } else {
      try {
        const created = await fetchApi<InventoryItem>(`/projects/${projectId}/inventory`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        const newItem: InventoryItem = {
          id: (created as any)?.id || `tmp-${Date.now()}`,
          ...payload,
          ...(created || {}),
        };
        setItems((prev) => [newItem, ...prev]);
        showToast(`${stLabel} added to inventory`, "success");
      } catch (err: any) {
        showToast(err.message || `Failed to add ${stLabel}`, "error");
      }
    }

    setShowAddItemModal(false);
    setEditingItemId(null);
    setItemFormFields({});
  };

  const handleDeleteInventoryItem = async (itemId: string) => {
    try {
      await fetchApi(`/projects/${projectId}/inventory/${itemId}`, { method: "DELETE" });
      setItems((prev) => prev.filter((it) => it.id !== itemId));
      showToast(`${stLabel} removed from inventory`, "success");
    } catch (err: any) {
      showToast(err.message || `Failed to delete ${stLabel}`, "error");
    }
  };

  // Link lead to specific unit
  const handleLinkLeadToUnit = async (lead: any) => {
    if (!linkLeadItemId) return;
    try {
      await fetchApi(`/projects/${projectId}/inventory/${linkLeadItemId}/link-lead`, {
        method: "POST",
        body: JSON.stringify({
          leadId: lead.id,
          leadName: lead.name,
          status: stSoldStatus,
        }),
      });

      setItems((prev) =>
        prev.map((it) =>
          it.id === linkLeadItemId
            ? { ...it, lead_id: lead.id, leadName: lead.name, buyer: lead.name, status: stSoldStatus }
            : it
        )
      );

      setAllLeads((prev) =>
        prev.map((l) => (l.id === lead.id ? { ...l, projectId, project_id: projectId } : l))
      );

      showToast(`Lead ${lead.name} linked to ${stLabel}`, "success");
    } catch (err: any) {
      showToast(err.message || "Failed to link lead", "error");
    } finally {
      setLinkLeadItemId(null);
    }
  };

  // Link lead directly to project
  const handleLinkLeadToProject = async (leadId: string | number) => {
    try {
      await fetchApi(`/projects/${projectId}/leads/link`, {
        method: "POST",
        body: JSON.stringify({ leadId }),
      });
      setAllLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, projectId, project_id: projectId } : l))
      );
      showToast("Lead linked to project successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to link lead to project", "error");
    } finally {
      setShowLinkLeadModal(false);
    }
  };

  // Unlink lead from project
  const handleUnlinkLeadFromProject = async (leadId: string | number) => {
    try {
      await fetchApi(`/projects/${projectId}/leads/${leadId}/unlink`, { method: "DELETE" });
      setAllLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, projectId: null, project_id: null } : l))
      );
      showToast("Lead unlinked from project", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to unlink lead", "error");
    }
  };

  // Project Tasks
  const handleCreateTask = async () => {
    if (!newTaskTitle.trim()) {
      showToast("Task title is required", "error");
      return;
    }
    try {
      const created = await fetchApi<any>(`/projects/${projectId}/tasks`, {
        method: "POST",
        body: JSON.stringify({
          title: newTaskTitle.trim(),
          assigned_to: newTaskAssigned.trim() || null,
          due_date: newTaskDueDate || null,
          priority: newTaskPriority,
          status: "To Do",
        }),
      });
      setTasks((prev) => [created, ...prev]);
      setNewTaskTitle("");
      setNewTaskAssigned("");
      setNewTaskDueDate("");
      setShowAddTask(false);
      showToast("Task added successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to create task", "error");
    }
  };

  const handleToggleTaskStatus = async (task: any) => {
    const nextStatus = task.status === "Completed" ? "To Do" : "Completed";
    try {
      await fetchApi(`/projects/${projectId}/tasks/${task.id}`, {
        method: "PUT",
        body: JSON.stringify({ status: nextStatus }),
      });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: nextStatus } : t)));
    } catch (err: any) {
      showToast(err.message || "Failed to update task", "error");
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await fetchApi(`/projects/${projectId}/tasks/${taskId}`, { method: "DELETE" });
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      showToast("Task deleted", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to delete task", "error");
    }
  };

  // Project Documents
  const handleAddDocument = async () => {
    if (!docName.trim()) {
      showToast("Document name is required", "error");
      return;
    }
    try {
      const res = await fetchApi<any>(`/projects/${projectId}/documents`, {
        method: "POST",
        body: JSON.stringify({
          name: docName.trim(),
          type: docType,
          size: docSize,
          url: docUrl || "#",
        }),
      });
      if (res?.allDocuments) {
        setDocuments(res.allDocuments);
      } else if (res?.data) {
        setDocuments((prev) => [res.data, ...prev]);
      }
      setDocName("");
      setDocUrl("");
      setShowAddDocModal(false);
      showToast("Document added successfully", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to add document", "error");
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    try {
      const res = await fetchApi<any>(`/projects/${projectId}/documents/${docId}`, { method: "DELETE" });
      if (res?.data) {
        setDocuments(res.data);
      } else {
        setDocuments((prev) => prev.filter((d) => d.id !== docId && d.name !== docId));
      }
      showToast("Document deleted", "success");
    } catch (err: any) {
      showToast(err.message || "Failed to delete document", "error");
    }
  };

  // Export inventory to CSV
  const handleExportInventory = () => {
    if (items.length === 0) {
      showToast("No inventory items to export", "error");
      return;
    }
    exportToCSV(items as Record<string, unknown>[], `${project?.name || "project"}_inventory_${formatExportDate()}.csv`);
    showToast("Inventory exported successfully", "success");
  };

  // Analytics Computations
  const analyticsData = useMemo(() => {
    const sourcesCount: Record<string, number> = {};
    projectLeads.forEach((l) => {
      const s = l.source || "Manual";
      sourcesCount[s] = (sourcesCount[s] || 0) + 1;
    });
    const totalProjLeads = projectLeads.length || 1;
    const sourceBreakdown = Object.entries(sourcesCount).map(([src, count]) => ({
      source: src,
      count,
      pct: Math.round((count / totalProjLeads) * 100),
    }));

    const stageCounts: Record<string, number> = {};
    projectLeads.forEach((l) => {
      const stg = l.stage || "New Inquiry";
      stageCounts[stg] = (stageCounts[stg] || 0) + 1;
    });

    const agentCounts: Record<string, { leads: number; won: number }> = {};
    projectLeads.forEach((l) => {
      const ag = l.assigned || "Unassigned";
      if (!agentCounts[ag]) agentCounts[ag] = { leads: 0, won: 0 };
      agentCounts[ag].leads += 1;
      if (l.stage === "Booking Done" || l.stage === "Closed Won" || l.stage === "Booked") {
        agentCounts[ag].won += 1;
      }
    });
    const agentsLeaderboard = Object.entries(agentCounts).map(([name, data]) => ({
      name,
      leads: data.leads,
      conversions: data.won,
      pct: data.leads > 0 ? Math.round((data.won / data.leads) * 100) : 0,
    }));

    return {
      sourceBreakdown,
      stageCounts,
      agentsLeaderboard,
    };
  }, [projectLeads]);

  if (loading) {
    return (
      <CrmLayout title="Project Details">
        <div className="p-16 text-center">
          <div className="w-10 h-10 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600">Loading project details...</p>
        </div>
      </CrmLayout>
    );
  }

  if (!project) {
    return (
      <CrmLayout title="Project Not Found">
        <div className="p-16 text-center max-w-md mx-auto">
          <div className="w-14 h-14 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-3 text-2xl">
            <i className="ri-error-warning-line" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-1">Project Not Found</h3>
          <p className="text-sm text-gray-500 mb-5">The project you are looking for does not exist or has been removed.</p>
          <button
            onClick={() => navigate("/projects")}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 cursor-pointer"
          >
            Back to Projects List
          </button>
        </div>
      </CrmLayout>
    );
  }

  const cfg = categoryConfigs[project.category as ProjectCategory] || categoryConfigs.properties;

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: "overview", label: "Overview" },
    { key: "leads", label: "Leads", count: projectLeads.length },
    { key: "pipeline", label: "Pipeline" },
    { key: "inventory", label: "Inventory", count: items.length },
    { key: "documents", label: "Documents", count: documents.length },
    { key: "analytics", label: "Analytics" },
    { key: "quotations", label: "Quotations", count: linkedQuotations.length },
  ];

  return (
    <CrmLayout title={project.name}>
      <div className="p-4 md:p-6 space-y-6">
        {/* Header Bar */}
        <div>
          <button
            onClick={() => navigate("/projects")}
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-indigo-600 mb-2 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-sm" /> Back to Projects
          </button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <div className={`w-3.5 h-12 rounded-full ${getColorClass(project.color)} flex-shrink-0 mt-0.5`} />
              <div>
                <div className="flex flex-wrap items-center gap-2.5">
                  <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
                  <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${getStatusBadge(project.status, project.category || 'properties')}`}>
                    {project.status}
                  </span>
                  <span className="px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full flex items-center gap-1">
                    <i className={`${cfg.icon} text-xs`} />
                    {cfg.title}
                  </span>
                  {sellingType && (
                    <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full flex items-center gap-1.5 ${getSellingTypeBgClass(sellingType.color)}`}>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getSellingTypeColorClass(sellingType.color)}`} />
                      {sellingType.name}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mt-1.5">
                  {project.client_name && <span>Client: <strong>{project.client_name}</strong></span>}
                  {project.budget > 0 && <span>Budget: <strong>₹{project.budget.toLocaleString('en-IN')}</strong></span>}
                  {project.start_date && <span>Start: <strong>{project.start_date}</strong></span>}
                  {project.due_date && <span>Due: <strong>{project.due_date}</strong></span>}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                onClick={openAddInventoryItem}
                className="px-3.5 py-2 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <i className="ri-add-line text-sm" /> Add {stLabel}
              </button>
              <button
                onClick={openEditModal}
                className="px-3.5 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <i className="ri-edit-line text-sm text-gray-500" /> Edit Project
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-3.5 py-2 text-xs font-medium text-red-600 bg-white border border-red-200 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <i className="ri-delete-bin-line text-sm text-red-500" /> Delete
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 overflow-x-auto gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap cursor-pointer transition-all border-b-2 flex items-center gap-1.5 ${
                activeTab === tab.key
                  ? "text-indigo-600 border-indigo-600 font-semibold"
                  : "text-gray-500 border-transparent hover:text-gray-800 hover:border-gray-300"
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${
                  activeTab === tab.key ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-600"
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab 1: OVERVIEW */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Project Custom Fields Grid */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-information-line text-indigo-600" /> Project Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-6">
                  {cfg.formFields.map((f) => {
                    const val = (project.customFields || {})[f.key];
                    if (!val) return null;
                    return (
                      <div key={f.key}>
                        <p className="text-xs text-gray-500 font-medium">{f.label}</p>
                        <p className="text-sm font-semibold text-gray-900 break-words mt-0.5">{val}</p>
                      </div>
                    );
                  })}
                  {project.client_name && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Client / Developer</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">{project.client_name}</p>
                    </div>
                  )}
                  {project.budget > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Allocated Budget</p>
                      <p className="text-sm font-semibold text-green-700 mt-0.5">₹{project.budget.toLocaleString('en-IN')}</p>
                    </div>
                  )}
                  {project.spent > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Budget Spent</p>
                      <p className="text-sm font-semibold text-amber-700 mt-0.5">₹{project.spent.toLocaleString('en-IN')}</p>
                    </div>
                  )}
                  {project.start_date && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Start Date</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">{project.start_date}</p>
                    </div>
                  )}
                  {project.due_date && (
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Due Date</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">{project.due_date}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {project.description && (
                <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-900 mb-2.5">About This Project</h3>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{project.description}</p>
                </div>
              )}

              {/* Interactive Tasks Checklist */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <i className="ri-task-line text-indigo-600" /> Project Milestones & Tasks ({tasks.length})
                  </h3>
                  <button
                    onClick={() => setShowAddTask(!showAddTask)}
                    className="px-2.5 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                  >
                    + Add Task
                  </button>
                </div>

                {showAddTask && (
                  <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                    <input
                      type="text"
                      placeholder="Task description (e.g. Schedule model flat walk-through)..."
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-indigo-500"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <select
                        value={newTaskAssigned}
                        onChange={(e) => setNewTaskAssigned(e.target.value)}
                        className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white outline-none cursor-pointer"
                      >
                        <option value="">👤 Select Assigned Agent</option>
                        {agentsList.map((a) => (
                          <option key={a} value={a}>👤 {a}</option>
                        ))}
                      </select>
                      <input
                        type="date"
                        value={newTaskDueDate}
                        onChange={(e) => setNewTaskDueDate(e.target.value)}
                        className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white outline-none"
                      />
                      <select
                        value={newTaskPriority}
                        onChange={(e) => setNewTaskPriority(e.target.value)}
                        className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white outline-none cursor-pointer"
                      >
                        <option value="Low">Low Priority</option>
                        <option value="Medium">Medium Priority</option>
                        <option value="High">High Priority</option>
                      </select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setShowAddTask(false)} className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded">
                        Cancel
                      </button>
                      <button onClick={handleCreateTask} className="px-3 py-1 text-xs bg-indigo-600 text-white rounded font-medium hover:bg-indigo-700">
                        Save Task
                      </button>
                    </div>
                  </div>
                )}

                {tasks.length === 0 ? (
                  <p className="text-xs text-gray-400 py-3 text-center">No tasks recorded for this project yet.</p>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {tasks.map((task) => (
                      <div key={task.id} className="py-2.5 flex items-center justify-between gap-3 group">
                        <div className="flex items-center gap-3 min-w-0">
                          <input
                            type="checkbox"
                            checked={task.status === "Completed"}
                            onChange={() => handleToggleTaskStatus(task)}
                            className="w-4 h-4 text-indigo-600 rounded cursor-pointer"
                          />
                          <div className="min-w-0">
                            <p className={`text-xs font-medium ${task.status === "Completed" ? "line-through text-gray-400" : "text-gray-900"}`}>
                              {task.title}
                            </p>
                            <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-0.5">
                              {task.assigned_to && <span>👤 {task.assigned_to}</span>}
                              {task.due_date && <span>📅 {task.due_date}</span>}
                              <span className={`px-1.5 py-0.2 rounded font-semibold ${
                                task.priority === "High" ? "bg-red-50 text-red-600" : "bg-gray-100 text-gray-600"
                              }`}>
                                {task.priority || "Medium"}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          className="text-gray-300 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Progress Gauge & Key Metrics */}
            <div className="space-y-6">
              {/* Dynamic Circular Progress Gauge */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-900 mb-4">
                  {project.category === "courses" ? "Enrollment Progress" : project.category === "plans" ? "Adoption Progress" : "Sales Progress"}
                </h3>
                <div className="flex items-center justify-center mb-4">
                  <div className="relative w-36 h-36">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#f1f5f9" strokeWidth="10" />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#4f46e5"
                        strokeWidth="10"
                        strokeLinecap="round"
                        strokeDasharray={`${dynamicProgress * 2.51} 251`}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-gray-900">{dynamicProgress}%</span>
                      <span className="text-[10px] text-gray-500 font-medium">Achieved</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-gray-100">
                  <div>
                    <p className="text-base font-bold text-indigo-600">{projectLeads.length}</p>
                    <p className="text-[10px] text-gray-500">Leads</p>
                  </div>
                  <div>
                    <p className="text-base font-bold text-green-600">{availableUnitsCount}</p>
                    <p className="text-[10px] text-gray-500">{stAvailableStatus}</p>
                  </div>
                  <div>
                    <p className="text-base font-bold text-purple-600">{soldUnitsCount}</p>
                    <p className="text-[10px] text-gray-500">{stSoldStatus}</p>
                  </div>
                </div>
              </div>

              {/* Linked Team Members */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-team-line text-indigo-600" /> Team & Agents
                </h3>
                <div className="space-y-2.5">
                  {analyticsData.agentsLeaderboard.length === 0 ? (
                    <p className="text-xs text-gray-400">No agents assigned to project leads yet.</p>
                  ) : (
                    analyticsData.agentsLeaderboard.map((a) => (
                      <div key={a.name} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-[10px]">
                            {a.name.slice(0, 2).toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-800">{a.name}</span>
                        </div>
                        <span className="text-gray-500">{a.leads} leads</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: LEADS */}
        {activeTab === "leads" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-sm text-gray-600">
                Showing <strong>{projectLeads.length}</strong> leads linked to {project.name}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowLinkLeadModal(true)}
                  className="px-3.5 py-2 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <i className="ri-link text-sm" /> Link Existing Lead
                </button>
                <button
                  onClick={() => navigate("/leads")}
                  className="px-3.5 py-2 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer shadow-sm"
                >
                  <i className="ri-add-line text-sm" /> View CRM Leads
                </button>
              </div>
            </div>

            {projectLeads.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
                <div className="w-12 h-12 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-3 text-xl">
                  <i className="ri-user-unfollow-line" />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">No Leads Linked Yet</h3>
                <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">
                  Link existing leads from your CRM or capture new inquiries for this project.
                </p>
                <button
                  onClick={() => setShowLinkLeadModal(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 cursor-pointer"
                >
                  Link a Lead Now
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">
                        <th className="px-4 py-3">Lead Name</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3">Stage</th>
                        <th className="px-4 py-3">Source</th>
                        <th className="px-4 py-3">Score</th>
                        <th className="px-4 py-3">Assigned Agent</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {projectLeads.map((l) => (
                        <tr
                          key={l.id}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/leads/${l.id}`)}
                        >
                          <td className="px-4 py-3 font-semibold text-indigo-600">{l.name}</td>
                          <td className="px-4 py-3 text-xs text-gray-600">{l.phone || "—"}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700">
                              {l.stage || "New Inquiry"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">{l.source || "Manual"}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getScoreBadgeColors(l.score || 50)}`}>
                              {l.score ?? 50} · {getScoreLabel(l.score || 50)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">{l.assigned || "Unassigned"}</td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleUnlinkLeadFromProject(l.id)}
                              className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors cursor-pointer"
                            >
                              Unlink
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab 3: PIPELINE */}
        {activeTab === "pipeline" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Dynamic Pipeline Kanban for <strong>{project.name}</strong></p>
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-3 overflow-x-auto">
              {pipelineStages.map((stage) => {
                const stageLeads = projectLeads.filter((l) => (l.stage || "New Inquiry").toLowerCase() === stage.name.toLowerCase());
                return (
                  <div key={stage.name} className="bg-gray-50/80 rounded-xl border border-gray-200 p-3 min-h-[320px] flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold text-gray-800 truncate">{stage.name}</span>
                      <span className="px-1.5 py-0.5 text-[10px] font-bold bg-white text-gray-700 rounded-full border border-gray-200">
                        {stageLeads.length}
                      </span>
                    </div>

                    <div className="space-y-2 flex-1 overflow-y-auto">
                      {stageLeads.map((l) => (
                        <div
                          key={l.id}
                          onClick={() => navigate(`/leads/${l.id}`)}
                          className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm hover:border-indigo-400 cursor-pointer transition-all space-y-1.5"
                        >
                          <p className="text-xs font-bold text-gray-900">{l.name}</p>
                          <p className="text-[10px] text-gray-500">{l.phone || "No phone"}</p>
                          <div className="flex items-center justify-between text-[10px] pt-1 border-t border-gray-100">
                            <span className="text-indigo-600 font-semibold">{l.source || "Direct"}</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${getScoreBadgeColors(l.score || 50)}`}>
                              {l.score ?? 50}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab 4: INVENTORY */}
        {activeTab === "inventory" && (
          <div className="space-y-4">
            {/* Inventory Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total {stLabel}s</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalUnitsCount}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <p className="text-xs font-semibold text-green-600 uppercase tracking-wider">{stAvailableStatus}</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{availableUnitsCount}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider">{stSoldStatus}</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{soldUnitsCount}</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {items.length} {stLabel.toLowerCase()}s in stock
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportInventory}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1.5 cursor-pointer"
                >
                  <i className="ri-download-line" /> Export CSV
                </button>
                <button
                  onClick={openAddInventoryItem}
                  className="px-3.5 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1.5 cursor-pointer shadow-sm"
                >
                  <i className="ri-add-line" /> Add {stLabel}
                </button>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
                <div className="w-12 h-12 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mx-auto mb-3 text-xl">
                  <i className="ri-inbox-line" />
                </div>
                <p className="text-sm font-bold text-gray-900">No {stLabel.toLowerCase()}s added yet</p>
                <p className="text-xs text-gray-500 mt-1 mb-4">Add individual inventory units to manage availability and sales.</p>
                <button
                  onClick={openAddInventoryItem}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 cursor-pointer"
                >
                  + Add First {stLabel}
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">
                        {inventoryListColumns.map((col) => (
                          <th key={getFieldKey(col)} className="px-4 py-3 whitespace-nowrap">{col.label}</th>
                        ))}
                        <th className="px-4 py-3 whitespace-nowrap">Buyer / Linked Lead</th>
                        <th className="px-4 py-3 text-right whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                          {inventoryListColumns.map((col) => {
                            const k = getFieldKey(col);
                            const val = item[k] !== undefined ? item[k] : item[col.fieldKey] !== undefined ? item[col.fieldKey] : item[col.label];
                            if (col.label === "Status" || k === "status" || col.fieldKey === "status") {
                              return (
                                <td key={k} className="px-4 py-3">
                                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ${getStatusBadgeColor(val || "")}`}>
                                    {val || "Available"}
                                  </span>
                                </td>
                              );
                            }
                            return (
                              <td key={k} className="px-4 py-3 text-xs text-gray-800 whitespace-nowrap">
                                {val || "—"}
                              </td>
                            );
                          })}
                          <td className="px-4 py-3 text-xs whitespace-nowrap">
                            {item.leadName || item.buyer ? (
                              <span className="font-semibold text-indigo-600">👤 {item.leadName || item.buyer}</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap relative">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => {
                                  setLinkLeadItemId(item.id);
                                  setLeadSearchQuery("");
                                }}
                                className="px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 rounded transition-colors cursor-pointer"
                                title="Link Lead"
                              >
                                Link Lead
                              </button>
                              <button
                                onClick={() => openEditInventoryItem(item)}
                                className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                                title="Edit Item"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteInventoryItem(item.id)}
                                className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                title="Delete Item"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab 5: DOCUMENTS */}
        {activeTab === "documents" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                <strong>{documents.length}</strong> project documents and brochures
              </p>
              <button
                onClick={() => setShowAddDocModal(true)}
                className="px-3.5 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <i className="ri-upload-line" /> + Upload / Add Document
              </button>
            </div>

            {documents.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
                <div className="w-12 h-12 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-3 text-xl">
                  <i className="ri-file-text-line" />
                </div>
                <p className="text-sm font-bold text-gray-900">No Documents Attached</p>
                <p className="text-xs text-gray-500 mt-1 mb-4">Attach project brochures, agreement drafts, and spec sheets.</p>
                <button
                  onClick={() => setShowAddDocModal(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 cursor-pointer"
                >
                  Upload First Document
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 shadow-sm">
                {documents.map((doc, i) => (
                  <div key={doc.id || i} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 text-xl">
                        <i className={fileIconMap[doc.type] || "ri-file-line text-indigo-600"} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{doc.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {doc.size || "1.2 MB"} • Uploaded {doc.uploaded || "Recently"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => showToast(`Opening document ${doc.name}...`, "info")}
                        className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-download-line mr-1" /> View / Download
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc.id || doc.name)}
                        className="p-1.5 text-xs text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab 6: ANALYTICS */}
        {activeTab === "analytics" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Lead Source Breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
              <h3 className="text-sm font-bold text-gray-900">Lead Source Breakdown</h3>
              {analyticsData.sourceBreakdown.length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">No source metrics available yet.</p>
              ) : (
                <div className="space-y-3">
                  {analyticsData.sourceBreakdown.map((s) => (
                    <div key={s.source}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium text-gray-700">{s.source}</span>
                        <span className="font-bold text-gray-900">{s.count} ({s.pct}%)</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${s.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Inventory Distribution */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
              <h3 className="text-sm font-bold text-gray-900">Inventory Status Distribution</h3>
              <div className="space-y-3.5">
                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-gray-700">{stAvailableStatus}</span>
                    <span className="font-bold text-green-600">{availableUnitsCount} units ({totalUnitsCount > 0 ? Math.round((availableUnitsCount / totalUnitsCount) * 100) : 0}%)</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${totalUnitsCount > 0 ? (availableUnitsCount / totalUnitsCount) * 100 : 0}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-gray-700">{stSoldStatus}</span>
                    <span className="font-bold text-purple-600">{soldUnitsCount} units ({totalUnitsCount > 0 ? Math.round((soldUnitsCount / totalUnitsCount) * 100) : 0}%)</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500 rounded-full"
                      style={{ width: `${totalUnitsCount > 0 ? (soldUnitsCount / totalUnitsCount) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Top Assigned Agents */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4 md:col-span-2">
              <h3 className="text-sm font-bold text-gray-900">Agent Performance for {project.name}</h3>
              {analyticsData.agentsLeaderboard.length === 0 ? (
                <p className="text-xs text-gray-400 py-6 text-center">No agent activities logged for this project yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {analyticsData.agentsLeaderboard.map((a) => (
                    <div key={a.name} className="p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-1">
                      <p className="text-xs font-bold text-gray-900">{a.name}</p>
                      <p className="text-xs text-gray-500">{a.leads} Total Leads</p>
                      <p className="text-xs font-semibold text-green-600">{a.conversions} Deals Closed ({a.pct}%)</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 7: QUOTATIONS */}
        {activeTab === "quotations" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                <strong>{linkedQuotations.length}</strong> quotations created for {project.name}
              </p>
              <button
                onClick={() => navigate("/quotations/new")}
                className="px-3.5 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1.5 cursor-pointer shadow-sm"
              >
                <i className="ri-add-line" /> Create Quotation
              </button>
            </div>

            {linkedQuotations.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center shadow-sm">
                <div className="w-12 h-12 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-3 text-xl">
                  <i className="ri-bill-line" />
                </div>
                <p className="text-sm font-bold text-gray-900">No Quotations Generated Yet</p>
                <p className="text-xs text-gray-500 mt-1 mb-4">Create formal proposals and quotes directly for project leads.</p>
                <button
                  onClick={() => navigate("/quotations/new")}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 cursor-pointer"
                >
                  Generate First Quotation
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">
                      <th className="px-4 py-3">Quote #</th>
                      <th className="px-4 py-3">Client</th>
                      <th className="px-4 py-3">Amount (₹)</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {linkedQuotations.map((q) => (
                      <tr key={q.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-semibold text-indigo-600">{q.quoteNumber || q.id}</td>
                        <td className="px-4 py-3 text-xs text-gray-800">{q.clientName}</td>
                        <td className="px-4 py-3 text-xs font-bold text-gray-900">₹{parseFloat(q.totalAmount || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-700">
                            {q.status || "Draft"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => navigate(`/quotations/${q.id}`)}
                            className="px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 rounded font-medium cursor-pointer"
                          >
                            View Quote
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Modal: Add / Edit Inventory Item */}
        {showAddItemModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    {editingItemId ? `Edit ${stLabel}` : `Add ${stLabel}`}
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {editingItemId
                      ? `Update details for this ${stLabel.toLowerCase()}`
                      : `Enter details to add a new ${stLabel.toLowerCase()} to ${project?.name || "the project"}`}
                  </p>
                </div>
                <button
                  onClick={() => setShowAddItemModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div className="p-6 space-y-4 flex-1">
                {inventoryFormFields.map((f) => {
                  const key = getFieldKey(f);
                  const isStatusField = key === "status" || f.label.toLowerCase() === "status";
                  
                  let options = f.options && f.options.length > 0 ? f.options : [];
                  if (isStatusField && options.length === 0) {
                    options = [stAvailableStatus || "Available", "Booked", stSoldStatus || "Sold", "On Hold"];
                  }

                  if (f.type === "Dropdown" || isStatusField) {
                    return (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {f.label} {f.required && <span className="text-red-500">*</span>}
                        </label>
                        <select
                          value={itemFormFields[key] !== undefined ? itemFormFields[key] : (isStatusField ? stAvailableStatus : "")}
                          onChange={(e) => setItemFormFields({ ...itemFormFields, [key]: e.target.value })}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none focus:border-indigo-500"
                        >
                          <option value="">Select {f.label}...</option>
                          {options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  if (f.type === "Currency") {
                    return (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {f.label} {f.required && <span className="text-red-500">*</span>}
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-medium">₹</span>
                          <input
                            type="text"
                            placeholder={f.placeholder || "e.g. 25,00,000 or 1.8 Cr"}
                            value={itemFormFields[key] || ""}
                            onChange={(e) => setItemFormFields({ ...itemFormFields, [key]: e.target.value })}
                            className="w-full text-sm border border-gray-200 rounded-lg pl-7 pr-3 py-2 outline-none focus:border-indigo-500"
                          />
                        </div>
                      </div>
                    );
                  }

                  if (f.type === "Textarea") {
                    return (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {f.label} {f.required && <span className="text-red-500">*</span>}
                        </label>
                        <textarea
                          rows={3}
                          placeholder={f.placeholder || `Enter ${f.label}`}
                          value={itemFormFields[key] || ""}
                          onChange={(e) => setItemFormFields({ ...itemFormFields, [key]: e.target.value })}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 resize-none"
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={key}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {f.label} {f.required && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type={f.type === "Number Input" ? "number" : f.type === "Date" ? "date" : "text"}
                        placeholder={f.placeholder || `Enter ${f.label}`}
                        value={itemFormFields[key] || ""}
                        onChange={(e) => setItemFormFields({ ...itemFormFields, [key]: e.target.value })}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500"
                      />
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white z-10">
                <button
                  onClick={() => setShowAddItemModal(false)}
                  className="px-4 py-2 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveInventoryItem}
                  className="px-5 py-2 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 cursor-pointer shadow-sm"
                >
                  {editingItemId ? "Save Changes" : `Add ${stLabel}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Link Lead to Unit */}
        {linkLeadItemId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <h3 className="text-sm font-bold text-gray-900">Link Lead to {stLabel}</h3>
                <button onClick={() => setLinkLeadItemId(null)} className="text-gray-400 hover:text-gray-600">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <input
                type="text"
                placeholder="Search CRM leads by name or phone..."
                value={leadSearchQuery}
                onChange={(e) => setLeadSearchQuery(e.target.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500"
              />

              <div className="max-h-56 overflow-y-auto space-y-1.5 divide-y divide-gray-50">
                {allLeads
                  .filter((l) =>
                    !leadSearchQuery.trim() ||
                    l.name?.toLowerCase().includes(leadSearchQuery.toLowerCase()) ||
                    l.phone?.includes(leadSearchQuery)
                  )
                  .slice(0, 8)
                  .map((lead) => (
                    <button
                      key={lead.id}
                      onClick={() => handleLinkLeadToUnit(lead)}
                      className="w-full text-left p-2.5 rounded-lg hover:bg-indigo-50/80 transition-colors flex items-center justify-between cursor-pointer group"
                    >
                      <div>
                        <p className="text-xs font-bold text-gray-900 group-hover:text-indigo-600">{lead.name}</p>
                        <p className="text-[10px] text-gray-500">{lead.phone || "No phone"} • {lead.stage}</p>
                      </div>
                      <span className="text-[10px] text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded">
                        Select
                      </span>
                    </button>
                  ))}
              </div>

              <div className="flex justify-end pt-2 border-t border-gray-100">
                <button
                  onClick={() => setLinkLeadItemId(null)}
                  className="px-3.5 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Link Lead to Project */}
        {showLinkLeadModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl p-5 space-y-4 max-h-[85vh] flex flex-col">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Link Existing CRM Lead to Project</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Select a lead from your CRM to link with {project.name}</p>
                </div>
                <button onClick={() => setShowLinkLeadModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              {/* Search input */}
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                <input
                  type="text"
                  placeholder="Search CRM leads by name, company, or phone..."
                  value={projectLeadSearch}
                  onChange={(e) => setProjectLeadSearch(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-lg pl-8 pr-3 py-2 outline-none focus:border-indigo-500 bg-gray-50/50 focus:bg-white"
                />
              </div>

              {/* Leads Selection List */}
              <div className="flex-1 overflow-y-auto space-y-2 max-h-64 pr-1">
                {(() => {
                  const filtered = allLeads.filter((l) => {
                    const isAlreadyLinked =
                      String(l.projectId) === String(projectId) ||
                      String(l.project_id) === String(projectId) ||
                      l.projectName === project.name;
                    if (isAlreadyLinked) return false;
                    if (!projectLeadSearch.trim()) return true;
                    const q = projectLeadSearch.toLowerCase();
                    return (
                      l.name?.toLowerCase().includes(q) ||
                      l.company?.toLowerCase().includes(q) ||
                      l.phone?.includes(q)
                    );
                  });

                  if (filtered.length === 0) {
                    return (
                      <div className="p-6 text-center text-xs text-gray-400">
                        {allLeads.length === 0
                          ? "No CRM leads found in the system."
                          : "No unlinked leads match your search."}
                      </div>
                    );
                  }

                  return filtered.map((lead) => (
                    <div
                      key={lead.id}
                      onClick={() => handleLinkLeadToProject(lead.id)}
                      className="p-3 rounded-xl border border-gray-100 hover:border-indigo-300 hover:bg-indigo-50/40 transition-all flex items-center justify-between gap-3 cursor-pointer group"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-bold text-gray-900 group-hover:text-indigo-600 truncate">{lead.name}</p>
                          {lead.company && (
                            <span className="text-[10px] text-gray-500 truncate">({lead.company})</span>
                          )}
                          <span className="text-[9px] font-semibold px-1.5 py-0.2 rounded bg-indigo-50 text-indigo-700 ml-auto">
                            Score: {lead.score ?? 50}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-1">
                          {lead.phone && <span>📞 {lead.phone}</span>}
                          {lead.stage && <span>• {lead.stage}</span>}
                          {lead.assigned && <span>• 👤 {lead.assigned}</span>}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLinkLeadToProject(lead.id);
                        }}
                        className="px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-xs transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Link Lead
                      </button>
                    </div>
                  ));
                })()}
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-100 text-xs">
                <span className="text-gray-400">{allLeads.length} total CRM leads available</span>
                <button
                  onClick={() => setShowLinkLeadModal(false)}
                  className="px-3.5 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Add Document */}
        {showAddDocModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <h3 className="text-sm font-bold text-gray-900">Add Project Document</h3>
                <button onClick={() => setShowAddDocModal(false)} className="text-gray-400 hover:text-gray-600">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Document Title *</label>
                <input
                  type="text"
                  placeholder="e.g. Master Floor Plan, RERA Clearance"
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none"
                  >
                    <option value="PDF">PDF</option>
                    <option value="DOCX">DOCX</option>
                    <option value="XLSX">XLSX</option>
                    <option value="IMAGE">IMAGE</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">File Size</label>
                  <input
                    type="text"
                    placeholder="e.g. 2.4 MB"
                    value={docSize}
                    onChange={(e) => setDocSize(e.target.value)}
                    className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Document URL / Storage Link</label>
                <input
                  type="text"
                  placeholder="https://..."
                  value={docUrl}
                  onChange={(e) => setDocUrl(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button
                  onClick={() => setShowAddDocModal(false)}
                  className="px-3.5 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddDocument}
                  className="px-4 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 cursor-pointer shadow-sm"
                >
                  Save Document
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Edit Project */}
        {showEditProjectModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                <h2 className="text-base font-bold text-gray-900">Edit Project Configuration</h2>
                <button onClick={() => setShowEditProjectModal(false)} className="text-gray-500 hover:text-gray-700">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <div className="p-6 space-y-4 flex-1">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Project Name *</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Status</label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none"
                    >
                      <option value="Active">Active</option>
                      <option value="Upcoming">Upcoming</option>
                      <option value="Draft">Draft</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Selling Type</label>
                    <select
                      value={editSellingTypeId}
                      onChange={(e) => setEditSellingTypeId(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none"
                    >
                      {sellingTypes.map((st) => (
                        <option key={st.id} value={st.id}>{st.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Client Name</label>
                    <input
                      type="text"
                      value={editClientName}
                      onChange={(e) => setEditClientName(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Budget (₹)</label>
                    <input
                      type="number"
                      value={editBudget}
                      onChange={(e) => setEditBudget(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={editStartDate}
                      onChange={(e) => setEditStartDate(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Due Date</label>
                    <input
                      type="date"
                      value={editDueDate}
                      onChange={(e) => setEditDueDate(e.target.value)}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
                  <textarea
                    rows={3}
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none resize-none"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white z-10">
                <button
                  onClick={() => setShowEditProjectModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProjectEdit}
                  className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-sm"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Delete Project Confirmation */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
              <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto text-2xl">
                <i className="ri-error-warning-line" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-bold text-gray-900">Delete Project?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Are you sure you want to delete <strong>{project.name}</strong>? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-center gap-3 pt-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteProject}
                  className="px-4.5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors cursor-pointer shadow-sm"
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CrmLayout>
  );
}